import { createSelector } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';

// Base selectors
const selectOrderState = state => state.orderState;

// Order data selectors
export const selectOrderData = createSelector(
  [selectOrderState],
  orderState => orderState.orderData
);

export const selectOrderField = fieldName =>
  createSelector([selectOrderData], orderData => orderData[fieldName]);

// Specific field selectors for convenience
export const selectCategory = createSelector([selectOrderData], orderData => orderData.category);

export const selectProviderUUID = createSelector(
  [selectOrderData],
  orderData => orderData.providerUserUUID
);

export const selectComments = createSelector([selectOrderData], orderData => orderData.comments);

export const selectAddress = createSelector([selectOrderData], orderData => orderData.address);

// SLIT specific selectors
export const selectSLITData = createSelector([selectOrderData], orderData => ({
  slitLowDose: orderData.slitLowDose,
  slitLowDoseQuantity: orderData.slitLowDoseQuantity,
  slitMaintenance: orderData.slitMaintenance,
  slitMaintenanceQuantity: orderData.slitMaintenanceQuantity,
}));

// SCIT specific selectors
export const selectSCITData = createSelector([selectOrderData], orderData => ({
  scitType: orderData.scitType,
  scitAllergens: orderData.scitAllergens,
  allergySymptoms: orderData.allergySymptoms,
  symptomsExposure: orderData.symptomsExposure,
  symptomsTimeOfYear: orderData.symptomsTimeOfYear,
  symptomsTimeInDay: orderData.symptomsTimeInDay,
  symptomsMostlyOccur: orderData.symptomsMostlyOccur,
  examinationReveals: orderData.examinationReveals,
  allergyDiagnosis: orderData.allergyDiagnosis,
}));

// Metadata selectors
export const selectPatientInfo = createSelector(
  [selectOrderState],
  orderState => orderState.patientInfo
);

export const selectProviders = createSelector(
  [selectOrderState],
  orderState => orderState.providers
);

export const selectProvidersAsMenuItems = createSelector([selectProviders], providers =>
  providers.map(provider => ({
    label: provider.name,
    value: provider.uuid,
  }))
);

export const selectAddresses = createSelector(
  [selectOrderState],
  orderState => orderState.addresses
);

// Permissions selectors
export const selectCanEnableImmunotherapyReport = createSelector(
  [selectOrderState],
  orderState => orderState.canEnableImmunotherapyReport
);

// Status selectors
export const selectOrderStatus = createSelector(
  [selectOrderState],
  orderState => orderState.status
);

export const selectCreateStatus = createSelector(
  [selectOrderState],
  orderState => orderState.createStatus
);

export const selectUpdateStatus = createSelector(
  [selectOrderState],
  orderState => orderState.updateStatus
);

export const selectProvidersStatus = createSelector(
  [selectOrderState],
  orderState => orderState.providersStatus
);

export const selectAddressesStatus = createSelector(
  [selectOrderState],
  orderState => orderState.addressesStatus
);

// Loading state selectors
export const selectIsOrderLoading = createSelector(
  [selectOrderStatus],
  status => status === REQUEST_STATUS.loading
);

export const selectIsOrderCreating = createSelector(
  [selectCreateStatus],
  status => status === REQUEST_STATUS.loading
);

export const selectIsOrderUpdating = createSelector(
  [selectUpdateStatus],
  status => status === REQUEST_STATUS.loading
);

export const selectIsProvidersLoading = createSelector(
  [selectProvidersStatus],
  status => status === REQUEST_STATUS.loading
);

export const selectIsAddressesLoading = createSelector(
  [selectAddressesStatus],
  status => status === REQUEST_STATUS.loading
);

export const selectIsAnyOperationLoading = createSelector(
  [
    selectIsOrderLoading,
    selectIsOrderCreating,
    selectIsOrderUpdating,
    selectIsProvidersLoading,
    selectIsAddressesLoading,
  ],
  (orderLoading, creating, updating, providersLoading, addressesLoading) =>
    orderLoading || creating || updating || providersLoading || addressesLoading
);

// Error selectors
export const selectOrderError = createSelector([selectOrderState], orderState => orderState.error);

export const selectBackendErrors = createSelector(
  [selectOrderState],
  orderState => orderState.backendErrors
);

export const selectBackendError = fieldName =>
  createSelector([selectBackendErrors], backendErrors => backendErrors[fieldName]);

// Form validation selectors
export const selectIsOrderFormValid = createSelector(
  [selectOrderData, selectBackendErrors],
  (orderData, backendErrors) => {
    const hasRequiredFields = orderData.providerUserUUID && orderData.category;
    const hasNoBackendErrors = Object.keys(backendErrors).length === 0;

    if (orderData.category === 'SCIT') {
      const hasSCITRequiredFields = orderData.scitType && orderData.scitAllergens?.length > 0;
      return hasRequiredFields && hasSCITRequiredFields && hasNoBackendErrors;
    }

    if (orderData.category === 'SLIT') {
      const hasSLITRequiredFields = orderData.slitLowDose || orderData.slitMaintenance;
      return hasRequiredFields && hasSLITRequiredFields && hasNoBackendErrors;
    }

    return hasRequiredFields && hasNoBackendErrors;
  }
);

// Computed data selectors
export const selectOrderFormData = createSelector(
  [selectOrderData, selectPatientInfo, selectProviders, selectAddresses],
  (orderData, patientInfo, providers, addresses) => ({
    values: orderData,
    metadata: {
      patientInfo,
      providers,
      addresses,
    },
  })
);
